// @ts-nocheck
import React, { useState, ChangeEvent, FormEvent, RefObject } from "react";
import styled, { css, ThemeProps, withTheme } from "styled-components";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { SelectPresentationProps } from "@insite/mobius/Select";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { connect, ResolveThunks } from "react-redux";
import FormActions, { FormActionsProps } from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import LoadingOverlay, { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import submitForm from "@store/Framework/Store/Store/Pages/Crm/Handlers/SubmitCrmForm";
import { FormFieldModel } from "@store/Framework/Types/ApiModels";
import { configurationFormProps } from "@store/Themes/common/props/configuratorForm";
import CustomerInformationFields, {
    CustomerFormErrors,
    CustomerFormState,
    emptyCustomerFormState,
    toCustomerInformationModel,
} from "@store/Library/Components/Forms/CustomerInformationFields";

const enum fields {
    title = "title",
    introText = "introText",
    questionnaireText = "questionnaireText",
    questionnaireUrl = "questionnaireUrl",
}

const enum optionFields {
    value = "value",
    label = "label",
}

export interface OptionItem {
    fields: {
        [optionFields.value]: string;
        [optionFields.label]?: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.introText]: string;
        [fields.questionnaireText]: string;
        [fields.questionnaireUrl]: string;
    };
}

const mapDispatchToProps = {
    submitForm,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ThemeProps<BaseTheme>;

interface FlsConfiguratorFormStyles {
    block?: BlockProps;
    container?: GridContainerProps;
    formContainer?: GridContainerProps;
    fieldItem?: GridItemProps;
    smallerFieldItem?: GridItemProps;
    fullWidthItem?: GridItemProps;
    actionItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    sectionText?: TypographyPresentationProps;
    introText?: TypographyPresentationProps;
    questionnaireText?: TypographyPresentationProps;
    identifierField?: TextFieldPresentationProps;
    selectField?: SelectPresentationProps;
    commentField?: TextAreaPresentationProps;
    clearButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
    successInfosBox?: InfosBoxProps;
    successModal?: ModalPresentationProps;
    continueButton?: ButtonPresentationProps;
    errorInfosBox?: InfosBoxProps;
    loadingOverlay?: LoadingOverlayProps;
    formActions?: FormActionsProps;
}

export const flsConfiguratorFormStyles: FlsConfiguratorFormStyles = {
    block: configurationFormProps.block,
    container: configurationFormProps.container,
    formContainer: configurationFormProps.formContainer,
    fieldItem: configurationFormProps.fieldItem,
    smallerFieldItem: configurationFormProps.smallerFieldItem,
    fullWidthItem: configurationFormProps.fullWidthItem,
    actionItem: configurationFormProps.actionItem,
    titleText: configurationFormProps.titleText,
    sectionText: configurationFormProps.sectionText,
    introText: configurationFormProps.introText,
    questionnaireText: configurationFormProps.questionnaireText,
    clearButton: configurationFormProps.clearButton,
    submitButton: configurationFormProps.submitButton,
    successInfosBox: configurationFormProps.successInfosBox,
    successModal: configurationFormProps.successModal,
    continueButton: configurationFormProps.continueButton,
    errorInfosBox: configurationFormProps.errorInfosBox,
    loadingOverlay: configurationFormProps.loadingOverlay,
    formActions: configurationFormProps.formActions,
};

const Spacer = styled.div`
    width: 100%;
    margin-bottom: ${pxToRem(12)};
    border-bottom: 1px solid ${getColor("common.border")};
`;

type FormState = CustomerFormState & {
    identifier: string;
    originalPOorSO: string;
    pumpApplication: string;
    weightPercentSolids: string;
    slurrySG: string;
    staticHead: string;
    flowRate: string;
    totalDynamicHead: string;
    comment: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const initialState: FormState = {
    ...emptyCustomerFormState(),
    identifier: "",
    originalPOorSO: "",
    pumpApplication: "",
    weightPercentSolids: "",
    slurrySG: "",
    staticHead: "",
    flowRate: "",
    totalDynamicHead: "",
    comment: "",
};

const toFormFields = (form: FormState): FormFieldModel[] => {
    return [
        { name: "FLS or Competitor Product Identifier", value: form.identifier },
        { name: "Original PO or SO number", value: form.originalPOorSO },
        { name: "Pump Application", value: form.pumpApplication },
        { name: "Weight % Solids", value: form.weightPercentSolids },
        { name: "Slurry SG", value: form.slurrySG },
        { name: "Static Head", value: form.staticHead },
        { name: "Flow Rate", value: form.flowRate },
        { name: "Total Dynamic Head", value: form.totalDynamicHead },
        { name: "Question or comment", value: form.comment, format: "long" },
    ];
};

const styles = flsConfiguratorFormStyles;

const overlayStyles = mergeProps(styles.loadingOverlay, {
    css: css`
        position: relative;
    `,
});

const FlsPumpConfiguratorForm = ({ fields, submitForm }: Props) => {
    const [form, setForm] = useState<FormState>(initialState);
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | undefined>(undefined);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

    const [customerErrors, setCustomerErrors] = useState<CustomerFormErrors>({});
    const [reloadCustomerInformationFields, setReloadCustomerInformationFields] = useState(true);

    const maxCommentLength = 2000;

    const handleChange =
        (fieldName: keyof FormState) =>
        (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
            const rawValue = event.target.value;
            const value =
                fieldName === "comment" && rawValue.length > maxCommentLength
                    ? rawValue.slice(0, maxCommentLength)
                    : rawValue;

            setForm(prev => ({ ...prev, [fieldName]: value }));

            if (errors[fieldName]) {
                setErrors(prev => ({ ...prev, [fieldName]: undefined }));
            }

            setReloadCustomerInformationFields(false);
        };

    const validate = (): boolean => {
        const newErrors: FormErrors = { ...customerErrors };
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleClear = () => {
        setForm(initialState);
        setErrors({});
        setSubmitError(undefined);
        setIsSuccessModalOpen(false);
        setReloadCustomerInformationFields(true);
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();

        setSubmitError(undefined);

        if (!validate()) {
            return;
        }

        setIsSubmitting(true);

        submitForm({
            formType: "PumpConfigurator",
            formFields: toFormFields(form),
            customerInformation: toCustomerInformationModel(form),
            onSuccess: onSubmitFormSuccess,
            onError: onSubmitFormError,
            onComplete: onSubmitFormComplete,
        });
    };

    const onSubmitFormSuccess = () => {
        setIsSuccessModalOpen(true);
    };

    const onSubmitFormComplete = () => {
        setIsSubmitting(false);
    };

    const onSubmitFormError = (error: string) => {
        setSubmitError(error);
    };

    const closeSuccessModal = () => {
        setIsSuccessModalOpen(false);
        handleClear();
    };

    const title = fields.title || translate("Configuration form");
    const intro = fields.introText;
    const questionnaireText =
        fields.questionnaireText ||
        translate("To view or download a complete version of the pump questionnaire, please click here:");

    const questionnaireUrl = fields.questionnaireUrl || "#";

    return (
        <Block {...styles.block}>
            <FormWrapper generalErrorMessage={submitError}>
                <LoadingOverlay {...overlayStyles} loading={isSubmitting}>
                    <GridContainer {...styles.formContainer} as="form" onSubmit={handleSubmit}>
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.titleText}>{title}</Typography>
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Spacer />
                        </GridItem>
                        <CustomerInformationFields
                            {...{
                                form,
                                updateCustomerState: customerState => {
                                    setForm(prev => ({ ...prev, ...customerState }));
                                },
                                onHandleChange: handleChange,
                                onValidationChange: (
                                    customerErrors: Partial<Record<keyof CustomerFormState, string>>,
                                ) => {
                                    setCustomerErrors(() => ({ ...customerErrors }));
                                },
                                errors,
                                reloadCustomerInformationFields,
                            }}
                        />
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.introText}>{intro}</Typography>
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <TextField
                                {...styles.identifierField}
                                label={translate("FlsPumpConfigurator.Identifier.Label")}
                                placeholder={translate("FlsPumpConfigurator.Identifier.Placeholder")}
                                value={form.identifier}
                                onChange={handleChange("identifier")}
                            />
                        </GridItem>
                        <GridItem {...styles.fieldItem}>
                            <TextField
                                {...styles.identifierField}
                                label={translate("FlsPumpConfigurator.OriginalPOorSO.Label")}
                                value={form.originalPOorSO}
                                onChange={handleChange("originalPOorSO")}
                            />
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.sectionText}>
                                {translate("FlsPumpConfigurator.ApplicationInfo.Label")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.smallerFieldItem}>
                            <TextField
                                {...styles.identifierField}
                                label={translate("FlsPumpConfigurator.PumpApplication.Label")}
                                value={form.pumpApplication}
                                onChange={handleChange("pumpApplication")}
                            />
                        </GridItem>
                        <GridItem {...styles.smallerFieldItem}>
                            <TextField
                                {...styles.identifierField}
                                label={translate("FlsPumpConfigurator.WeightPercentSolids.Label")}
                                value={form.weightPercentSolids}
                                onChange={handleChange("weightPercentSolids")}
                            />
                        </GridItem>
                        <GridItem {...styles.smallerFieldItem}>
                            <TextField
                                {...styles.identifierField}
                                label={translate("FlsPumpConfigurator.SlurrySG.Label")}
                                value={form.slurrySG}
                                onChange={handleChange("slurrySG")}
                            />
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.sectionText}>
                                {translate("FlsPumpConfigurator.PipeRouting.Label")}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.smallerFieldItem}>
                            <TextField
                                {...styles.identifierField}
                                label={translate("FlsPumpConfigurator.StaticHead.Label")}
                                value={form.staticHead}
                                onChange={handleChange("staticHead")}
                            />
                        </GridItem>
                        <GridItem {...styles.smallerFieldItem}>
                            <TextField
                                {...styles.identifierField}
                                label={translate("FlsPumpConfigurator.FlowRate.Label")}
                                value={form.flowRate}
                                onChange={handleChange("flowRate")}
                            />
                        </GridItem>
                        <GridItem {...styles.smallerFieldItem}>
                            <TextField
                                {...styles.identifierField}
                                label={translate("FlsPumpConfigurator.TotalDynamicHead.Label")}
                                value={form.totalDynamicHead}
                                onChange={handleChange("totalDynamicHead")}
                            />
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <TextArea
                                {...styles.commentField}
                                label={translate("Question or comment")}
                                placeholder={translate("Placeholder")}
                                value={form.comment}
                                onChange={handleChange("comment")}
                                rows={4}
                                hint={`${maxCommentLength - form.comment.length} ${translate("characters left")}`}
                            />
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Typography {...styles.questionnaireText}>
                                {questionnaireText}{" "}
                                <Link href={questionnaireUrl} target="_blank" rel="noopener noreferrer">
                                    {translate("click here")}
                                </Link>
                                {"."}
                            </Typography>
                        </GridItem>
                        <GridItem {...styles.fullWidthItem}>
                            <Spacer />
                        </GridItem>
                        <GridItem {...styles.actionItem}>
                            <FormActions {...styles.formActions}>
                                <Button
                                    {...styles.clearButton}
                                    type="button"
                                    onClick={handleClear}
                                    data-test-selector="flsConfigurator_clear"
                                >
                                    {translate("Clear")}
                                </Button>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isSubmitting || isSuccessModalOpen}
                                    data-test-selector="flsConfigurator_requestQuote"
                                >
                                    {isSubmitting ? translate("Submitting...") : translate("Request a quote")}
                                </Button>
                            </FormActions>
                        </GridItem>
                    </GridContainer>
                    <Modal
                        headline={translate("Request sent")}
                        contentLabel={translate("Your configuration has been submitted.")}
                        {...styles.successModal}
                        isOpen={isSuccessModalOpen}
                        handleClose={closeSuccessModal}
                    >
                        <InfosBox {...styles.successInfosBox}>
                            {translate(
                                "Your configuration has been submitted. A representative will contact you shortly.",
                            )}
                        </InfosBox>
                        <FormActions>
                            <Button
                                {...styles.continueButton}
                                onClick={closeSuccessModal}
                                data-test-selector="flsConfigurator_continue"
                            >
                                {translate("Continue")}
                            </Button>
                        </FormActions>
                    </Modal>
                </LoadingOverlay>
            </FormWrapper>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(() => ({}), mapDispatchToProps)(withTheme(FlsPumpConfiguratorForm)),
    definition: {
        group: "Products",
        icon: "form",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Configuration form",
                fieldType: "Contextual",
            },
            {
                name: fields.introText,
                displayName: "Intro text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                name: fields.questionnaireText,
                displayName: "Questionnaire text",
                editorTemplate: "TextField",
                defaultValue: "To view or download a complete version of the pump questionnaire, please",
                fieldType: "Contextual",
            },
            {
                name: fields.questionnaireUrl,
                displayName: "Questionnaire URL",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
